/**
 * Motor de sincronizacion: maquina de estados de la cola de salida (outbox),
 * politica de reintentos y cursor de entrada.
 *
 * Todo lo que decide "que sigue" vive aqui, puro y probado. La PWA solo ejecuta
 * las transiciones y persiste el resultado en IndexedDB; la API solo aplica las
 * operaciones que recibe. Ninguna pantalla llama a la red por su cuenta.
 */

import { diffMs, type Instant, plusSeconds } from './clock.js';
import { assertDomain } from './errors.js';
import type { Ulid } from './ids.js';

// ---------------------------------------------------------------------------
// Estados de la cola de salida
// ---------------------------------------------------------------------------

export const OUTBOX_STATUSES = [
  'PENDIENTE', // esperando turno
  'PROCESANDO', // enviado, sin respuesta confirmada aun
  'SINCRONIZADO', // el servidor confirmo y persistio
  'RECHAZADO', // error permanente de negocio; no se reintenta solo
  'CONFLICTO', // requiere decision (administrativa o del usuario)
] as const;
export type OutboxStatus = (typeof OUTBOX_STATUSES)[number];

export const OUTBOX_STATUS_LABELS: Readonly<Record<OutboxStatus, string>> = Object.freeze({
  PENDIENTE: 'Pendiente de enviar',
  PROCESANDO: 'Enviando',
  SINCRONIZADO: 'Sincronizado',
  RECHAZADO: 'Rechazado',
  CONFLICTO: 'Requiere revisión',
});

const ALLOWED_TRANSITIONS: Readonly<Record<OutboxStatus, readonly OutboxStatus[]>> = Object.freeze({
  // PROCESANDO -> PENDIENTE cubre la recuperacion tras un cierre abrupto.
  PENDIENTE: ['PROCESANDO', 'RECHAZADO', 'CONFLICTO'],
  PROCESANDO: ['SINCRONIZADO', 'PENDIENTE', 'RECHAZADO', 'CONFLICTO'],
  SINCRONIZADO: [],
  // Solo se reintenta tras una correccion valida.
  RECHAZADO: ['PENDIENTE'],
  CONFLICTO: ['PENDIENTE', 'RECHAZADO'],
});

export function canTransition(from: OutboxStatus, to: OutboxStatus): boolean {
  return (ALLOWED_TRANSITIONS[from] ?? []).includes(to);
}

export function assertTransition(from: OutboxStatus, to: OutboxStatus): void {
  assertDomain(
    canTransition(from, to),
    'TRANSICION_SYNC_INVALIDA',
    `Transición de sincronización inválida: ${from} -> ${to}`,
    { from, to },
  );
}

/** Estados terminales: no vuelven a intentarse automaticamente. */
export function isTerminal(status: OutboxStatus): boolean {
  return status === 'SINCRONIZADO' || status === 'RECHAZADO';
}

// ---------------------------------------------------------------------------
// Entradas de la cola
// ---------------------------------------------------------------------------

export const OPERATION_TYPES = [
  'PAGO',
  'ANULACION',
  'REIMPRESION',
  'ESTUDIANTE_ALTA',
  'ESTUDIANTE_EDICION',
  'ESTUDIANTE_ESTADO',
] as const;
export type OperationType = (typeof OPERATION_TYPES)[number];

export interface OutboxEntry {
  readonly id: Ulid;
  readonly type: OperationType;
  /** Clave idempotente estable: reintentar con la misma clave da el mismo resultado logico. */
  readonly idempotencyKey: string;
  /** Payload original, conservado tal cual para poder auditar divergencias. */
  readonly payload: unknown;
  readonly status: OutboxStatus;
  readonly attempts: number;
  readonly createdAt: Instant;
  readonly updatedAt: Instant;
  /** Instante a partir del cual se puede reintentar (backoff). */
  readonly nextAttemptAt: Instant | null;
  /** Operaciones que deben sincronizarse antes que esta (orden causal). */
  readonly dependsOn: readonly Ulid[];
  readonly lastErrorCode: string | null;
  readonly lastErrorMessage: string | null;
}

// ---------------------------------------------------------------------------
// Politica de reintentos
// ---------------------------------------------------------------------------

export interface RetryPolicy {
  readonly maxAttempts: number;
  readonly baseDelaySeconds: number;
  readonly maxDelaySeconds: number;
  /** Proporcion de jitter aleatorio (0..1) para evitar tormentas de reintentos. */
  readonly jitterRatio: number;
}

export const DEFAULT_RETRY_POLICY: RetryPolicy = Object.freeze({
  maxAttempts: 8,
  baseDelaySeconds: 2,
  maxDelaySeconds: 900,
  jitterRatio: 0.25,
});

/**
 * Backoff exponencial con jitter. `random` es inyectable para pruebas deterministas.
 * Devuelve segundos, nunca menor que `baseDelaySeconds / 2`.
 */
export function backoffSeconds(
  attempt: number,
  policy: RetryPolicy = DEFAULT_RETRY_POLICY,
  random: () => number = Math.random,
): number {
  const exponential = policy.baseDelaySeconds * 2 ** Math.max(0, attempt - 1);
  const capped = Math.min(exponential, policy.maxDelaySeconds);
  const jitter = capped * policy.jitterRatio * (random() * 2 - 1);
  return Math.max(policy.baseDelaySeconds / 2, Math.round((capped + jitter) * 100) / 100);
}

/**
 * Clasificacion del error devuelto por el servidor o la red.
 *  - TRANSITORIO: red caida, 5xx, timeout, 429 -> se reintenta con backoff.
 *  - PERMANENTE:  400/403/422 por regla de negocio -> se marca RECHAZADO, sin reintentos.
 *  - CONFLICTO:   409 o rechazo por version/saldo -> pasa a revision, sin reintentos.
 */
export type FailureKind = 'TRANSITORIO' | 'PERMANENTE' | 'CONFLICTO';

export function classifyFailure(params: {
  httpStatus: number | null;
  errorCode?: string | null;
}): FailureKind {
  const { httpStatus, errorCode } = params;
  if (httpStatus === null) return 'TRANSITORIO'; // sin respuesta = red
  if (httpStatus === 409) return 'CONFLICTO';
  if (httpStatus === 408 || httpStatus === 425 || httpStatus === 429) return 'TRANSITORIO';
  if (httpStatus >= 500) return 'TRANSITORIO';
  if (errorCode === 'CONFLICTO_VERSION' || errorCode === 'CONFLICTO_REQUIERE_REVISION') {
    return 'CONFLICTO';
  }
  if (httpStatus >= 400) return 'PERMANENTE';
  return 'TRANSITORIO';
}

// ---------------------------------------------------------------------------
// Transiciones
// ---------------------------------------------------------------------------

/**
 * Selecciona el siguiente lote a enviar respetando:
 *  - solo entradas PENDIENTE cuyo `nextAttemptAt` ya vencio,
 *  - orden causal: no se envia una entrada si alguna de la que depende no esta SINCRONIZADA,
 *  - una entrada bloqueada NO bloquea a las independientes,
 *  - orden FIFO por `createdAt` para preservar la secuencia del cajero.
 */
export function selectBatch(
  entries: readonly OutboxEntry[],
  now: Instant,
  batchSize = 25,
): OutboxEntry[] {
  const byId = new Map(entries.map((entry) => [entry.id, entry]));
  const ready = entries.filter((entry) => {
    if (entry.status !== 'PENDIENTE') return false;
    if (entry.nextAttemptAt !== null && diffMs(now, entry.nextAttemptAt) > 0) return false;
    return entry.dependsOn.every((dependencyId) => {
      const dependency = byId.get(dependencyId);
      // Una dependencia ya desaparecida se considera cumplida (ya se sincronizo y purgo).
      return dependency === undefined || dependency.status === 'SINCRONIZADO';
    });
  });
  return ready
    .sort((a, b) => (a.createdAt === b.createdAt ? a.id.localeCompare(b.id) : a.createdAt < b.createdAt ? -1 : 1))
    .slice(0, batchSize);
}

export function markProcessing(entry: OutboxEntry, now: Instant): OutboxEntry {
  assertTransition(entry.status, 'PROCESANDO');
  return { ...entry, status: 'PROCESANDO', attempts: entry.attempts + 1, updatedAt: now };
}

/**
 * Confirmacion del servidor. Es el UNICO camino a SINCRONIZADO: nunca se marca
 * local sin una respuesta valida y persistida del servidor.
 */
export function markSynced(entry: OutboxEntry, now: Instant): OutboxEntry {
  assertTransition(entry.status, 'SINCRONIZADO');
  return {
    ...entry,
    status: 'SINCRONIZADO',
    updatedAt: now,
    nextAttemptAt: null,
    lastErrorCode: null,
    lastErrorMessage: null,
  };
}

export function markFailed(
  entry: OutboxEntry,
  params: {
    now: Instant;
    kind: FailureKind;
    errorCode: string | null;
    errorMessage: string | null;
    policy?: RetryPolicy;
    random?: () => number;
  },
): OutboxEntry {
  const policy = params.policy ?? DEFAULT_RETRY_POLICY;
  const base = {
    ...entry,
    updatedAt: params.now,
    lastErrorCode: params.errorCode,
    lastErrorMessage: params.errorMessage,
  };

  if (params.kind === 'CONFLICTO') {
    assertTransition(entry.status, 'CONFLICTO');
    return { ...base, status: 'CONFLICTO', nextAttemptAt: null };
  }
  if (params.kind === 'PERMANENTE') {
    assertTransition(entry.status, 'RECHAZADO');
    return { ...base, status: 'RECHAZADO', nextAttemptAt: null };
  }
  // Transitorio: se agotan los intentos y despues se detiene (nunca infinito).
  if (entry.attempts >= policy.maxAttempts) {
    assertTransition(entry.status, 'RECHAZADO');
    return {
      ...base,
      status: 'RECHAZADO',
      nextAttemptAt: null,
      lastErrorMessage:
        params.errorMessage ?? 'No fue posible sincronizar tras varios intentos. Requiere revisión.',
    };
  }
  assertTransition(entry.status, 'PENDIENTE');
  return {
    ...base,
    status: 'PENDIENTE',
    nextAttemptAt: plusSeconds(
      params.now,
      backoffSeconds(entry.attempts, policy, params.random ?? Math.random),
    ),
  };
}

/**
 * Recuperacion tras cierre abrupto: toda entrada que quedo en PROCESANDO vuelve a
 * PENDIENTE. Es seguro porque la clave idempotente evita duplicar el pago si el
 * servidor si lo habia recibido.
 */
export function recoverInterrupted(entries: readonly OutboxEntry[], now: Instant): OutboxEntry[] {
  return entries.map((entry) =>
    entry.status === 'PROCESANDO'
      ? { ...entry, status: 'PENDIENTE' as const, updatedAt: now, nextAttemptAt: null }
      : entry,
  );
}

/** Reintento manual tras una correccion. Unico camino de salida de RECHAZADO/CONFLICTO. */
export function retryAfterCorrection(
  entry: OutboxEntry,
  now: Instant,
  correctedPayload?: unknown,
): OutboxEntry {
  assertTransition(entry.status, 'PENDIENTE');
  return {
    ...entry,
    status: 'PENDIENTE',
    attempts: 0,
    updatedAt: now,
    nextAttemptAt: null,
    payload: correctedPayload ?? entry.payload,
    lastErrorCode: null,
    lastErrorMessage: null,
  };
}

// ---------------------------------------------------------------------------
// Resumen para el centro de sincronizacion
// ---------------------------------------------------------------------------

export interface SyncSummary {
  readonly pending: number;
  readonly processing: number;
  readonly synced: number;
  readonly rejected: number;
  readonly conflicted: number;
  readonly oldestPendingAt: Instant | null;
}

export function summarize(entries: readonly OutboxEntry[]): SyncSummary {
  let oldestPendingAt: Instant | null = null;
  const counts = { PENDIENTE: 0, PROCESANDO: 0, SINCRONIZADO: 0, RECHAZADO: 0, CONFLICTO: 0 };
  for (const entry of entries) {
    counts[entry.status] += 1;
    if (entry.status === 'PENDIENTE' && (oldestPendingAt === null || entry.createdAt < oldestPendingAt)) {
      oldestPendingAt = entry.createdAt;
    }
  }
  return {
    pending: counts.PENDIENTE,
    processing: counts.PROCESANDO,
    synced: counts.SINCRONIZADO,
    rejected: counts.RECHAZADO,
    conflicted: counts.CONFLICTO,
    oldestPendingAt,
  };
}

// ---------------------------------------------------------------------------
// Sincronizacion de entrada (pull incremental)
// ---------------------------------------------------------------------------

export const SYNC_ENTITIES = [
  'institucion',
  'ciclos',
  'grados',
  'secciones',
  'estudiantes',
  'conceptos',
  'cargos',
  'saldos',
  'pagos',
] as const;
export type SyncEntity = (typeof SYNC_ENTITIES)[number];

export interface SyncCursor {
  readonly entity: SyncEntity;
  /** Marca de version del servidor a partir de la cual pedir cambios. */
  readonly since: Instant | null;
  readonly lastSyncedAt: Instant | null;
}

/** Antigüedad de los datos locales, para avisar al usuario que podrian estar desactualizados. */
export function stalenessSeconds(cursor: SyncCursor, now: Instant): number | null {
  if (cursor.lastSyncedAt === null) return null;
  return Math.max(0, Math.floor(diffMs(cursor.lastSyncedAt, now) / 1000));
}

/** Umbral por defecto a partir del cual la PWA marca los datos como "posiblemente desactualizados". */
export const STALE_THRESHOLD_SECONDS = 4 * 60 * 60;

export function isStale(
  cursor: SyncCursor,
  now: Instant,
  thresholdSeconds = STALE_THRESHOLD_SECONDS,
): boolean {
  const age = stalenessSeconds(cursor, now);
  return age === null || age > thresholdSeconds;
}
